import React from 'react';
import { Helmet } from 'react-helmet-async';
import styles from './PujaSection.module.css';
import Card from '../shared/Card';
import GlowButton from '../shared/GlowButton';

/**
 * PujaSection — Cards for featured puja services.
 * Each card's "Book Now" navigates to Booking with pre-selected type.
 * SEO: Proper headings (H2, H3), keyword-rich descriptions, and unique IDs.
 */

const pujaServices = [
    {
        id: 'ganesh-puja',
        icon: '🙏',
        title: 'Ganesh Puja',
        description: 'Invoke Lord Ganesha\'s blessings to remove obstacles and ensure success. Book a pandit for Ganesh Puja online across Mumbai, Delhi, and all of India.',
        bookingType: 'Ganesh Puja',
    },
    {
        id: 'lakshmi-puja',
        icon: '🪷',
        title: 'Lakshmi Puja',
        description: 'Invite Goddess Lakshmi\'s divine blessings for wealth, abundance, and prosperity. Professional pandit booking for Lakshmi Puja at your home.',
        bookingType: 'Lakshmi Puja',
    },
    {
        id: 'rudra-abhishek',
        icon: '🔱',
        title: 'Rudra Abhishek',
        description: 'Powerful Rudra Abhishek with sacred offerings to Lord Shiva for health and spiritual upliftment. Experienced pandit for havan and abhishek.',
        bookingType: 'Rudra Abhishek',
    },
    {
        id: 'wedding-puja',
        icon: '💍',
        title: 'Wedding Puja',
        description: 'Looking for a pandit for your Hindu wedding? Book a trusted vivah puja pandit online for sacred marriage rituals, pheras, and all wedding ceremonies.',
        bookingType: 'Wedding Puja',
    },
    {
        id: 'griha-pravesh',
        icon: '🏠',
        title: 'Griha Pravesh',
        description: 'How to book a pandit for griha pravesh? Simply choose this service! Expert housewarming puja with Vastu Shanti for your new home across India.',
        bookingType: 'Griha Pravesh',
    },
    {
        id: 'satyanarayan-katha',
        icon: '📖',
        title: 'Satyanarayan Katha',
        description: 'Book a pandit for Satyanarayan Katha at home. Authentic puja narration for prosperity, family well-being, and fulfilment of desires. Available across all cities.',
        bookingType: 'Satyanarayan Katha',
    },
    {
        id: 'havan-homam',
        icon: '🔥',
        title: 'Havan / Homam',
        description: 'Online pandit booking for havan and homam ceremonies. Sacred fire rituals performed by experienced pandits for purification and divine blessings.',
        bookingType: 'Havan / Homam',
    },
    {
        id: 'mundan-ceremony',
        icon: '✂️',
        title: 'Mundan Ceremony',
        description: 'Book a verified mundan pandit for your child\'s first hair-cutting ceremony. Traditional mundan sanskar performed with Vedic rituals and mantras.',
        bookingType: 'Mundan Ceremony',
    },
    {
        id: 'namkaran-ceremony',
        icon: '👶',
        title: 'Namkaran Ceremony',
        description: 'Sacred namkaran puja pandit booking for your newborn\'s naming ceremony. Vedic rituals to bless your baby with an auspicious name and divine protection.',
        bookingType: 'Namkaran Ceremony',
    },
    {
        id: 'satsang-pravachan',
        icon: '🙏',
        title: 'Satsang / Pravachan',
        description: 'Book an experienced satsang pandit for spiritual discourses and religious gatherings. Inspiring pravachan sessions for community and family events.',
        bookingType: 'Satsang / Pravachan',
    },
    {
        id: 'shradh-puja',
        icon: '🪔',
        title: 'Pitru Paksha / Shradh',
        description: 'Book a shradh puja pandit for Pitru Paksha rituals. Honour your ancestors with authentic tarpan, pind daan, and shradh ceremonies performed by learned pandits.',
        bookingType: 'Pitru Paksha / Shradh',
    },
];

export default function PujaSection({ onBookWithType }) {
    return (
        <section className={styles.pujaSection} id="puja-services-section">
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Our Popular Puja Services</h2>
                <p className={styles.sectionSubtitle}>
                    Book verified pandits online for authentic Hindu puja ceremonies, havans, and sacred rituals across India
                </p>
            </div>
            <div className={styles.cardsGrid}>
                {pujaServices.map((service) => (
                    <Card key={service.id} className={styles.pujaCard}>
                        <div className={styles.cardIcon}>{service.icon}</div>
                        <div className={styles.cardContent}>
                            <h3 className={styles.cardTitle}>{service.title}</h3>
                            <p className={styles.cardDescription}>{service.description}</p>
                            <GlowButton
                                text="Book Now"
                                variant="secondary"
                                small
                                className={styles.bookButton}
                                onClick={() => onBookWithType(service.bookingType)}
                                id={`book-${service.id}-btn`}
                            />
                        </div>
                    </Card>
                ))}
            </div>

            {/* How It Works — SEO H2 */}
            <div className={styles.howItWorks}>
                <h2 className={styles.sectionTitle}>How It Works</h2>
                <p className={styles.sectionSubtitle}>
                    Book a pandit online in 3 simple steps — the best pandit booking app in India
                </p>
                <div className={styles.stepsGrid}>
                    <div className={styles.stepCard}>
                        <span className={styles.stepNumber}>1</span>
                        <h3 className={styles.stepTitle}>Choose Your Puja</h3>
                        <p className={styles.stepDesc}>
                            Select from our wide range of puja services — wedding, griha pravesh, satyanarayan katha, havan, and more.
                        </p>
                    </div>
                    <div className={styles.stepCard}>
                        <span className={styles.stepNumber}>2</span>
                        <h3 className={styles.stepTitle}>Fill Your Details</h3>
                        <p className={styles.stepDesc}>
                            Share your name, date, location, and any special requests. We serve all major cities across India.
                        </p>
                    </div>
                    <div className={styles.stepCard}>
                        <span className={styles.stepNumber}>3</span>
                        <h3 className={styles.stepTitle}>Get a Verified Pandit</h3>
                        <p className={styles.stepDesc}>
                            We assign a verified, experienced pandit near you. Confirm via WhatsApp and enjoy authentic rituals.
                        </p>
                    </div>
                </div>
            </div>

            {/* Why Choose Us — SEO H2 */}
            <div className={styles.whyChoose}>
                <h2 className={styles.sectionTitle}>Why Choose PanditJi.space</h2>
                <p className={styles.sectionSubtitle}>
                    India's trusted online pandit service for marriage, griha pravesh, havan, and all Hindu ceremonies
                </p>
                <div className={styles.stepsGrid}>
                    <div className={styles.stepCard}>
                        <span className={styles.stepIcon}>✅</span>
                        <h3 className={styles.stepTitle}>100% Verified Pandits</h3>
                        <p className={styles.stepDesc}>
                            Every pandit on our platform is personally verified for knowledge of Vedic scriptures, experience, and authenticity.
                        </p>
                    </div>
                    <div className={styles.stepCard}>
                        <span className={styles.stepIcon}>🇮🇳</span>
                        <h3 className={styles.stepTitle}>Pan-India Coverage</h3>
                        <p className={styles.stepDesc}>
                            Find a pandit near me — we cover Mumbai, Delhi, Bangalore, Pune, Hyderabad, Chennai, Kolkata, Jaipur, and 500+ cities.
                        </p>
                    </div>
                    <div className={styles.stepCard}>
                        <span className={styles.stepIcon}>💰</span>
                        <h3 className={styles.stepTitle}>Transparent Pricing</h3>
                        <p className={styles.stepDesc}>
                            No hidden charges. We share dakshina and samagri details upfront. Pandit booking for housewarming puja made affordable.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
